import { is } from 'bpmn-js/lib/util/ModelUtil';

/** Match "BRE3", "BRE10 – Sonderfall" etc. case-insensitively. */
const BRE_CODE_RE = /^\s*(BRE\d+)\b/i;

/**
 * Resolve the BRE code of an element from custom:breCode (set by the converter)
 * with a fallback to the event name. Returns an upper-cased code or null.
 */
export function getBreCode(element) {
  const bo = element?.businessObject;
  if (!bo) return null;
  const attr = bo.get && bo.get('custom:breCode');
  if (attr) return String(attr).toUpperCase();
  const match = BRE_CODE_RE.exec(bo.name || '');
  return match ? match[1].toUpperCase() : null;
}

/** True for the catch side that DESCRIBES a BRE (the handler entry). */
export function isBreHandlerEntry(element) {
  return is(element, 'bpmn:StartEvent') && !!getBreCode(element);
}

/** True for the throw/boundary side that USES a BRE inside a process. */
export function isBreUsage(element) {
  if (!element || element.type === 'label' || is(element, 'bpmn:StartEvent')) return false;
  return !!getBreCode(element);
}

/** Numeric-aware sort key, so BRE2 comes before BRE10. */
export function breSortKey(code) {
  const n = parseInt(String(code).replace(/\D/g, ''), 10);
  return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
}

function safeRoots(canvas) {
  try { return canvas.getRootElements?.() ?? []; } catch { return []; }
}

/** Resolve the plane (root element) that contains `target`, across all planes. */
function rootOf(canvas, target) {
  try {
    const found = canvas.findRoot?.(target);
    if (found) return found;
  } catch { /* fall through to heuristic */ }
  const parentBo = target.businessObject?.$parent;
  const planeId = parentBo ? `${parentBo.id}_plane` : null;
  const roots = safeRoots(canvas);
  return roots.find((r) => r.id === planeId) || (target.parent && roots.find((r) => r === target.parent)) || null;
}

/**
 * Switch to the plane that contains `target`, scroll it into view and briefly
 * highlight it. The element is intentionally NOT selected — a selection frame and
 * context pad would obscure the highlighted target.
 */
export function navigateToElement(canvas, target) {
  if (!target) return;
  try {
    const root = rootOf(canvas, target);
    if (root && canvas.getRootElement?.() !== root) {
      canvas.setRootElement(root);
    }
    if (typeof canvas.scrollToElement === 'function') {
      canvas.scrollToElement(target);
    } else {
      canvas.zoom?.('fit-viewport');
    }
    flash(canvas, target);
  } catch (err) {
    console.warn('[BRE] navigation failed', err);
  }
}

/**
 * Records, per BRE code, the exact process element a forward jump started from,
 * so the handler can navigate back to that precise origin (a BRE can be used by
 * many events). Module-level so the panel and the click provider share it.
 */
export const breOrigins = new Map();

function flash(canvas, target) {
  try {
    canvas.addMarker(target, 'bre-highlight');
    setTimeout(() => {
      try { canvas.removeMarker(target, 'bre-highlight'); } catch { /* element gone */ }
    }, 2500);
  } catch { /* canvas not ready */ }
}
